import React from 'react';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import Image from 'next/legacy/image';
// import ReadingTime from '@/components/ReadingTime/ReadingTime';
import SideCardContent from '@/components/SideCardContent/SideCardContent';
import SideCardCTA from '@/components/SideCardCTA/SideCardCTA2';
import ImageOne from 'images/solar-guide/image19.jpg';
import ImageTwo from 'images/solar-guide/image68.png';
import ImageThree from 'images/solar-guide/image71.png';
import ImageFour from 'images/solar-guide/image34.png';
import ImageFive from 'images/solar-guide/image5.png';
import ImageSix from 'images/solar-guide/image12.png';
import ImageEight from 'images/solar-guide/image50.png';
import ImageNine from 'images/solar-guide/image32.png';
import Image11 from 'images/solar-guide/image58.png';
import Image12 from 'images/solar-guide/image49.jpg';
import Link from 'next/link';
import FullWidthFormSection from '@/components/LeadForm/FullWidthFormSection';
import FullWidthGuideFormSection from '@/components/LeadForm/FullWidthGuideForm';
import ReadingTime from '@/components/ReadingTime/ReadingTime';
import { SolarGuideTable } from './why-go-solar';

const Page = () => {
  return (
    <article>
      <MetaTags
        title="Battery Storage Products "
        description="2023 Solar Guide"
      />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Home Solar Guide',
            href: '/home-solar-guide',
          },
          {
            name: '🔋 Battery Storage Products ',
            href: '/home-solar-guide/battery-storage-products',
          },
        ]}
      />
      <CenteredContentSection>
        <div className="flex justify-between items-center">
          <Link href={'/home-solar-guide/solar-panels'}>← Solar Panels</Link>
          <Link href={'/home-solar-guide/steps-to-install-a-home-solar-system'}>
            Steps to Install a Home Solar System →
          </Link>
        </div>
        <section id="section1">
          <H3>🔋 Battery Storage Products </H3>
          <div className="mb-6">
            <ReadingTime wordCount={3067} />
          </div>
          <Image
            className="drop-shadow-lg rounded-md"
            src={ImageOne}
            alt="Solar battery in garage"
          />
          <p>
            While batteries are not required for net-metered solar systems their
            popularity is increasing exponentially. We can thank all of the
            advances in Electric Vehicle battery technology in reducing the cost
            per kilowatt-hour for battery capacity. The next section we will{' '}
            <strong>
              deep-dive on home batteries, their components, popular
              manufacturers
            </strong>
            , and more nuances regarding batteries. We will also highlight the
            benefits you receive when choosing to add a battery to your solar
            system.{' '}
          </p>
          <H4>How Home Batteries Work</H4>
          <p>
            A home battery acts like a barrier between your home and the power
            grid. It plugs right into your current electrical system to optimize
            daily energy use and to provide backup power during a grid failure.
            It does this by taking in power from the grid or your solar panels
            and then strategically deploying it on your house.
          </p>
          <p>
            Home batteries have been around for decades but like the cell phone
            before the smartphone, home batteries in the past were bulky,
            expensive, inconvenient, and limited in functionality.
          </p>
          <p>
            The principle of a home battery system is very simple, but there are
            some interesting nuances, particularly seasonal behavior which are
            worthy of further explanation.
          </p>
          <H4>Electricity Loads within the home</H4>
          <p>
            In order to understand the benefits of a home battery system it is
            first necessary to understand what you are using. Electricity use
            within an individual home tends to be very spiky, especially when
            high-powered appliances such as kettles, vacuums, air conditioning,
            or dryers are used. We also see higher usage when the house is
            occupied which for a working family will be predominantly at
            breakfast and in the evening. This is when lighting and other
            appliances such as TVs, and consumer electronics are on. The diagram
            below shows a typical profile of a house through the course of a
            day:
          </p>
          <Image src={ImageTwo} alt="Electricity Loads within the home" />
          <p>
            The image above shows a homeowner charging up their battery by noon
            and exporting power to the grid except for a momentary spike from a
            clothes dryer during the day. In the evening a typical-use family
            would charge their electric vehicle using a mixture of solar and
            battery power and top off their battery by the end of the evening.
            At night when the sun is down, the battery would self-consume their
            stored solar power until the cycle repeats itself.
          </p>
          <p>
            Depending on where a homeowner lives they may not be able to be
            truly “self-sufficient” off of their solar system. In parts of the
            “Sun Belt” (CA, AZ) year round self-consumption of solar through
            their battery might be possible.
          </p>
          <H4>Home Battery Components</H4>
          <p>
            Solar + storage systems require a number of additional components to
            make them work. While there are many variations the basic components
            for any solar + storage system are:
          </p>
          <H4>Solar Panels</H4>
          <p>
            Solar panels allow you to charge your battery with clean renewable
            energy. You can have an energy storage system without solar panels
            but having solar panels is preferable.
          </p>
          <H4>Batteries</H4>
          <p>
            The battery is the center of your energy storage system. The two
            most common types of batteries are Lithium-Iron-Phosphate (LFP) and
            Lithium- Nickel-Magnesium-Cobalt (NMC).
          </p>
          <H4>Inverters</H4>
          <p>
            <Link href="/solar-faq/what-are-solar-inverters">Inverters</Link>{' '}
            are devices that convert energy from AC to DC. Inverters are needed
            to convert the DC energy from your solar panels and batteries into
            AC energy that is used in the home. Some batteries and solar panels
            now include a built-in inverter in their components.
          </p>
          <H4>Critical Load Subpanel</H4>
          <p>
            A critical load subpanel is a device that determines what will get
            powered if the grid goes out. Most batteries can’t power an entire
            home for very long.{' '}
            <strong>
              To extend the amount of time your battery lasts during an outage
              you can choose to power only a few critical appliances in your
              home (such as the refrigerator and lights).
            </strong>{' '}
            The critical load sub panel allows you to choose those critical
            loads and it’s very important that solar installers set this up
            correctly for your battery’s capacity. Many times we have to upgrade
            a homeowners main electrical panel when adding a battery system to
            their home.
          </p>
          <H4>Other Components</H4>
          <p>
            There are many smaller components needed to make a solar + storage
            system work such as AC & DC disconnects, fuses, circuit breakers,
            switchgear, cables, electrical conduit, mounting hardware, battery
            enclosures, and safety equipment. Most modern solar storage systems
            even include software and mobile apps to give even more control to
            the homeowner.
          </p>
          <p>
            We are often asked,{' '}
            <Link
              href={'/solar-faq/how-long-can-a-solar-battery-power-your-house'}
            >
              “How long will I run on battery power?”
            </Link>{' '}
            or &rsquo;How long will the battery last?&rsquo; The answer is,
            &rsquo;The battery system could run your house indefinitely as long
            as you don&lsquo;t try to run too many things simultaneously!&rsquo;
          </p>
          <H4>Battery Coupling</H4>
          <p>
            Solar panels and battery storage equipment work with direct current
            (DC) and the grid and the outlets in a home use alternating current
            (AC).
          </p>
          <p>
            To make a solar panel and battery system work with the grid, the
            system’s DC power must be converted to AC power. This is done using
            a device called an inverter. The opposite device which converts AC
            to DC is called a rectifier. In solar plus storage the term{' '}
            <strong>coupling</strong> refers to which type of electricity is
            used to tie the solar and battery together.{' '}
          </p>
          <p>
            In a DC Coupled system, the battery and solar are tied together
            using DC electricity and share a single inverter/rectifier as per
            the diagram below:
          </p>
          <Image src={ImageThree} alt="DC-Coupled Batteries" />
          <H4>Advantages of DC-Coupled Batteries</H4>
          <p>
            A battery can be installed into a system either on the DC or AC
            side. The benefits of installing a battery on the DC side is that
            the battery and solar panels can both use the same inverter, which
            saves money on equipment and labor costs. DC-coupled systems also
            tend to be more energy efficient and lower priced.
          </p>
          <p>
            In an AC coupled system, the battery and solar are tied together
            using AC electricity and share separate Inverter/Rectifiers as per
            the diagram below:
          </p>
          <Image src={ImageFour} alt="AC-Coupled Batteries" />

          <H4>Advantages of AC-Coupled Batteries</H4>
          <p>
            There are some cases where you may want to install the battery on
            the AC side. If a solar panel system has already been installed on a
            home, you may want to use an AC-coupled battery so that you don’t
            have to rewire the existing solar system. AC- coupled systems are
            also easier to add to in the future and provide more control since
            the battery uses its own inverter.
          </p>
          <p>
            <strong>
              If you are using microinverters on a solar system, then the
              storage options are limited to AC coupled options.
            </strong>{' '}
            This is because the inverter is attached directly to each panel and
            there isn’t an opportunity to work with the DC electricity prior to
            it arriving at the inverter.
          </p>
          <p>
            Knowing these differences can help you know what energy storage
            solution you should choose. Different markets have access to
            different inverter solutions, sometimes due to permitting or utility
            approval.
          </p>
          <H4>Battery Manufacturers</H4>
          <p>
            The most popular battery manufacturer in the past few years has been
            Tesla, with their Powerwall. In recent times the supply of Powerwall
            has become constrained, and the viability of several other AC
            coupled batteries has improved. Today there are many other companies
            producing great quality battery systems with arguably equivalent or
            better chemistries and at attractive pricing per unit of power
            stored. Below are the main batteries on the market today divided by
            AC or DC coupling which affects how they are installed with solar.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="py-3 px-6 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AC-Coupled Batteries
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="py-4 px-6 whitespace-nowrap">
                    Tesla Powerwall 2
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 whitespace-nowrap">
                    Enphase Encharge
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 whitespace-nowrap">
                    Sonnen SonnenCore
                  </td>
                </tr>
              </tbody>
            </table>

            <table className="min-w-full mt-8 divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="py-3 px-6 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    DC-Coupled Batteries
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="py-4 px-6 whitespace-nowrap">
                    LG Chem / Sunrun BrightBox
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 whitespace-nowrap">
                    Generac PWRcell
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Image src={ImageFive} alt="Tesla power wall batteries" />
          <H4>Battery Location</H4>
          <p>
            Typically when installing a battery on your house, you have several
            options. The most popular installation locations are on the side of
            the house, in the garage, and in the basement.{' '}
            <strong>
              When selecting the installation location, temperature is a
              significant factor.
            </strong>{' '}
            Most batteries can operate in ambient temperatures from around -15º
            C to 55º C however{' '}
            <strong>maximum performance occurs from 0º C to 30º C.</strong> If
            you live in a very hot area you would not want to place your battery
            in a location which gets a lot of direct sunlight. If you live in a
            very cold area you may not want to or be allowed to place your
            battery outdoors.
          </p>
          <Image src={ImageSix} alt="" />
          <H4>Home Electrical System</H4>
          <p>
            When building out a solar power system on your property, you have to
            ensure that we are able to safely connect to the existing home
            electrical distribution panel. Many times for large PV Solar
            systems, upgrades to an electrical panel are needed. However some
            systems could be much more complex and expensive, rendering a
            project not viable after a site survey review.{' '}
          </p>
          <p>
            Understanding the main panel and your home&lsquo;s power system can
            be critical in determining what options are available to you,{' '}
            <strong>especially with battery systems.</strong>
            <p>
              <strong>
                Talk to us to figure out if a home solar and battery system is
                right for you:
              </strong>
            </p>
          </p>
        </section>
      </CenteredContentSection>
      <div className="mt-[-60px]">
        <FullWidthFormSection cid="a4oqmxxt5un31ha0" />
      </div>
      <CenteredContentSection>
        <H4>Battery Chemistry Types</H4>
        <p>
          Lithium-ion batteries are preferred in the solar industry because they
          can discharge deeper and have more cycles than traditional lead-acid
          batteries. They’re the same type of batteries found in your cellphone,
          laptop, and other electronics.{' '}
          <strong>
            A typical lithium-ion battery will give you around 6,000-10,000
            cycles at 90% discharge — so they will have a lifespan of roughly
            13-18 years.
          </strong>
        </p>
        <p>
          The two most common types of lithium-ion batteries are
          Lithium-Iron-Phosphate (LFP) and Lithium-Nickel-Magnesium-Cobalt
          (NMC).
        </p>
        <p>
          <strong>LFP batteries</strong> are the most commonly used battery type
          and are preferred for their stability, durability, and performance.
          They are less prone to thermal runaway, also known as exploding, so
          they are generally the safer option.These home batteries use the same
          advanced lithium ion battery cells that power electric cars like
          Tesla, Chevy Volt, and Nissan Leaf. We work with the best battery
          technology companies in the world providing compact, automated,
          cloud-connected home battery systems that require no maintenance and
          last over 10 years.
        </p>
        <div className="w-72 mx-auto">
          <Image src={ImageEight} alt="LFP Battery Diagram" />
        </div>
        <p>
          <strong>NMC batteries</strong> are the second most commonly used and
          are preferred for their high energy density, lower cost, and long
          cycle life. Many companies that already produce batteries for other
          applications, such as Tesla, prefer NMC batteries because they already
          have the infrastructure to produce them.
        </p>
        <div className="w-72 mx-auto">
          <Image src={ImageNine} alt="NMC Battery" />
        </div>
        <H4>Battery Warranties</H4>
        <p>
          Similar to your laptop or cell phone battery, solar batteries degrade
          over time. As you continue to use and charge your batteries, they lose
          the ability to hold a charge. Fortunately solar battery manufacturers
          provide warranties that guarantee the performance of a battery to a
          certain level. In this section we will discuss the key elements of a
          battery system that are typically covered under a warranty.
        </p>
        <p>
          EnergyPal only offers lithium-ion based battery systems that have
          minimum 10 year warranty terms. Most homeowners taking advantage of
          solar-plus-storage systems charge and drain their batteries daily –
          this is known as a &ldquo;<strong>cycle</strong>.&ldquo; As an
          addition to guaranteeing battery life for a set amount of years, some
          battery manufacturers also provide a warranty for guaranteed
          performance over a certain number of cycles.{' '}
        </p>
        <p>
          However it’s important to not evaluate battery warranties based on
          cycles alone because solar batteries gradually lose their ability to
          hold a full charge as they age. A cycle towards the end of a battery’s
          life will store and produce less energy than the output of a cycle
          immediately after installation.{' '}
          <strong>
            The size of the battery is also an important factor to compare
            alongside cycles.
          </strong>{' '}
          A smaller battery that has a lifespan of 10,000 cycles may actually
          deliver less energy over its lifespan than a larger battery warrantied
          for only 7,000 cycles. That’s why it’s also important to consider a
          battery’s <strong>throughput warranty.</strong>
        </p>
        <p>
          A <strong>“throughput” warranty</strong> is the total energy a
          manufacturer expects the battery to deliver throughout its lifetime.
          These warranties are typically stated in terms of megawatt-hours
          (MWh). Let’s say that a battery manufacturer provides a throughput
          warranty of 20 MWh. This means that the warranty is valid until the
          battery stores and delivers 20 MWh–or 20,000 kilowatt-hours (kWh)–of
          energy.
        </p>
        <p>
          In addition to providing a warranty for a set number of years, cycles,
          or throughput, select manufacturers also provide an{' '}
          <strong>end of warranty capacity rating.</strong> Typically this will
          be 60 to 70 percent of its original capacity. Higher percentages are
          better than lower percentage guarantees.
        </p>
        <H3>Benefits of a Battery</H3>

        <p>
          Up until now, we’ve focused on the “what” and “how” of batteries. This
          next section will help you understand “Why batteries?”.
        </p>
        <p>
          <strong>
            There are three motivations on why homeowners get a solar battery
            system.
          </strong>{' '}
        </p>
        <ol>
          <li>
            <strong>Peace of mind</strong> by having backup power
          </li>
          <li>Additional savings through energy rate arbitrage</li>
          <li>Additional incentives from battery specific funding programs</li>
        </ol>

        <H4>Peace of Mind</H4>
        <p>
          Unexpected{' '}
          <Link
            href={'/solar-faq/can-i-use-solar-energy-during-a-power-outage'}
          >
            power outages
          </Link>{' '}
          can range from a mild nuisance to an absolute disaster for a
          homeowner. Recently, we saw how unprepared homes in Texas were when
          there were cold snaps and wide-spread outages. Homes without backup
          power fell into disarray with pipes freezing and millions of dollars
          of damages.{' '}
        </p>
        <p>
          For homeowners in California who regularly face power shut-offs due to
          wind conditions posing a fire risk to the state, there is a frequent
          frustration with spoiled food and interrupted work-days.{' '}
        </p>
        <p>
          Having a reliable backup option could make a huge difference to a
          homeowner. As home solar systems are really just about producing
          cheaper electricity the conversation is much more analytical and
          economic in nature. The decision to get a battery is more emotional.{' '}
          <strong>
            Who can put a price on the peace of mind of having 24/7 reliable
            power for a home?
          </strong>
        </p>
        <p>
          It is important to note that most battery systems do not back up the
          entirety of a home’s electrical needs. Specific high-power items such
          as clothes dryers, ovens, or air conditioning are often excluded.{' '}
          <strong>
            A home battery system is intended to cover critical loads (lights,
            fridges, internet, computer & phone charging).
          </strong>
        </p>
        <div className="mx-auto w-96"></div>
        <H4>Time of Use Rate Arbitrage</H4>
        <Image src={Image11} alt="Time of Use Rate Arbitrage Diagram" />

        <p>
          Many utility companies, especially on the west coast, offer
          time-of-use rate plans. These rate plans charge people more during
          “peak” times and less during “off-peak times”. A homeowner with a home
          solar battery system would be able to charge up their batteries during
          low rate times and discharge their batteries power into their homes
          during peak times. This process can be controlled through software
          automatically and enables the homeowner to reduce their grid energy
          usage at peak times.{' '}
        </p>
        <p>
          <strong>
            As a rule of thumb, the bigger the difference in off-peak and
            on-peak rates the larger the arbitrage opportunity.
          </strong>{' '}
          There are considerations to be made as the process of storing power in
          the battery and discharging power from the battery results in some
          losses and therefore increased savings from a battery really only
          works when there is a decent difference between the on-peak and
          off-peak rates. This can make sense for homeowners who purchase energy
          from Southern California Edison and enroll in their TOU-D-PRIME rate
          plan which has a 24¢/kWh off-peak rate and a 62¢/kWh on-peak rate - a
          significant range!
        </p>
        <H4>Battery Incentives</H4>
        <p>
          Homeowners aren’t the only group who benefit from having a battery
          connected to their solar system as utilities can also benefit. As home
          solar systems become more popular an issue known as the “Duck Curve”
          arises. The Duck Curve refers to the shape of demand for utility
          generated power over the course of a typical day. The need for utility
          generated electricity in the middle of the day is low as home solar
          production peaks and sends excess energy to the grid. In the evening
          as home solar systems are producing less and families return home
          demand on the grid increases. This uneven demand characterized by a
          lull followed by a spike creates the shape of the back of a duck (see
          image below). This low demand followed by high demand is hard for
          utility owned power plants to respond to as most power plants deliver
          a high level of consistent power and cannot be powered down or up
          easily.
        </p>
        <Image src={Image12} alt="Net load chart" />
        <p>
          Fortunately home battery storage solutions can help create more
          consistent demand on the grid. Instead of home solar systems sending
          tons of power to the grid in the middle of the day during peak solar
          production hours,{' '}
          <strong>
            homes with solar batteries can self-charge their battery and reduce
            grid overload.
          </strong>
        </p>
        <p>
          This load shifting can impact the grid in a way to help utility
          companies improve their profits and therefore not only does the
          homeowner gain security and potential additional savings from their
          battery but they are also helping build a more resilient and
          sustainable grid.
        </p>
        <p>
          As a result of these benefits some utilities have direct cash
          incentives to install batteries. Typically this will be based on the
          usable capacity (kWh) rating of the battery. For example, a 250 $/kWh
          rebate for a 13 kWh battery would produce a $3,250 rebate to a
          homeowner which is a significant cost offset!
        </p>
        <p>
          Another incentive provided to some homeowners to install batteries is
          performance-based. These programs are called “Demand Response”
          programs. Here’s how it works. Imagine you were running the power grid
          for your state. During the middle of the day on a hot summer
          afternoon, everyone is demanding electricity. Your nuclear plants are
          constant and can’t increase their power output safely in time. The sun
          is out but you can’t get any more solar power. Your natural gas plants
          are firing but they are at their power limit.
        </p>
        <p>
          You have two options. You could rapidly turn-on an infrequently used
          coal “peaker” plant which is expensive and idling for 99% of the year
          or you could call upon thousands of homeowners to discharge their
          stored solar power into the grid to increase the supply -- responding
          to the demand.
        </p>
        <p>
          These demand response programs will pay a homeowner based on the
          frequency of “events” where they called upon the battery and reward is
          based on the power (kW) rating of the batteries. Some homeowners are
          getting thousands of dollars a year off their battery systems just
          helping the grid avoid turning on infrequently used, higher carbon
          emitting power sources.
        </p>
      </CenteredContentSection>
      <SideCardContent titles={['The Difficulty of Installation']} />
      <SideCardCTA />
      <FullWidthGuideFormSection cid={'a4oqmxxt5un31ha0'} />
      <SolarGuideTable />
    </article>
  );
};

export default Page;
